// Token de sesión de la web.
//
// Login normal: se guarda cifrado en localStorage para que la sesión sobreviva a cerrar el
// navegador (en el celular, sessionStorage se pierde cuando Android o iOS cierran Chrome).
// Se cifra con AES-GCM (Web Crypto) usando una llave NO exportable guardada en IndexedDB:
// si alguien copia lo guardado a otro navegador o dispositivo, no puede leerlo.
// Se renueva cada vez que se abre o se vuelve a la web y vence tras SESSION_MAX_IDLE_MS sin
// abrirla: para quien la usa nunca vence, y un celular abandonado no queda con acceso.
// Al cerrar sesión (o con un 403) se borra también la llave.
// Si el navegador no permite cifrar, la sesión vive solo en memoria (nunca sin cifrar).
// No protege contra un script malicioso dentro de la misma página; eso requeriría una
// cookie HttpOnly emitida por el backend.
//
// QR de emergencia: solo en sessionStorage, sin cifrar y sin persistir; es temporal y lo usa
// quien escaneó la moto de otra persona.
//
// El token se usa como JSON (igual que antes): quien lo lee hace JSON.parse.

const ENCRYPTED_KEY = 'trakku.session'
const TEMPORARY_KEY = 'token'
const DB_NAME = 'trakku-auth'
const DB_STORE = 'keys'
const CRYPTO_KEY_ID = 'session'
const SESSION_MAX_IDLE_MS = 30 * 24 * 60 * 60 * 1000
// Con la página abierta mucho tiempo, se renueva al volver a ella, como máximo cada 12 h.
const RENEW_EVERY_MS = 12 * 60 * 60 * 1000
let renewedAt = 0

// Token descifrado, solo en memoria mientras la página está abierta.
let memoryToken = null

const attempt = (action, fallback = null) => {
  try {
    return action()
  } catch (error) {
    return fallback
  }
}

const toBase64 = (buffer) => btoa(String.fromCharCode(...new Uint8Array(buffer)))
const fromBase64 = (text) => Uint8Array.from(atob(text), (char) => char.charCodeAt(0))

const openKeyStore = () => new Promise((resolve, reject) => {
  const request = window.indexedDB.open(DB_NAME, 1)
  request.onupgradeneeded = () => request.result.createObjectStore(DB_STORE)
  request.onsuccess = () => resolve(request.result)
  request.onerror = () => reject(request.error)
})

const keyStoreRequest = async (mode, run) => {
  const db = await openKeyStore()
  return new Promise((resolve, reject) => {
    const request = run(db.transaction(DB_STORE, mode).objectStore(DB_STORE))
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

const deleteCryptoKey = () =>
  keyStoreRequest('readwrite', (store) => store.delete(CRYPTO_KEY_ID)).catch(() => {})

const getCryptoKey = async ({ create }) => {
  const stored = await keyStoreRequest('readonly', (store) => store.get(CRYPTO_KEY_ID))
  if (stored || !create) return stored || null
  const key = await window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false, // no exportable
    ['encrypt', 'decrypt']
  )
  await keyStoreRequest('readwrite', (store) => store.put(key, CRYPTO_KEY_ID))
  return key
}

const encrypt = async (text) => {
  const key = await getCryptoKey({ create: true })
  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const data = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(text)
  )
  return JSON.stringify({ v: 1, iv: toBase64(iv), data: toBase64(data) })
}

const decrypt = async (payload) => {
  const { iv, data } = JSON.parse(payload)
  const key = await getCryptoKey({ create: false })
  if (!key) throw new Error('Sin llave de sesión en este navegador')
  const plain = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64(iv) },
    key,
    fromBase64(data)
  )
  return new TextDecoder().decode(plain)
}

const persistToken = (token) => {
  renewedAt = Date.now()
  return encrypt(JSON.stringify({ token, savedAt: renewedAt }))
    .then((payload) => window.localStorage.setItem(ENCRYPTED_KEY, payload))
}

const renewOnReturn = () => {
  if (document.visibilityState !== 'visible' || !memoryToken) return
  if (!attempt(() => window.localStorage.getItem(ENCRYPTED_KEY))) return
  if (Date.now() - renewedAt < RENEW_EVERY_MS) return
  persistToken(memoryToken).catch(() => {})
}

// Se llama una vez antes de mostrar la app (src/index.js) para recuperar el login guardado.
export const restoreSession = async () => {
  document.addEventListener('visibilitychange', renewOnReturn)
  const payload = attempt(() => window.localStorage.getItem(ENCRYPTED_KEY))
  if (!payload) return
  try {
    const { token, savedAt } = JSON.parse(await decrypt(payload))
    if (!token || !(Date.now() - savedAt < SESSION_MAX_IDLE_MS)) throw new Error('Sesión vencida')
    memoryToken = token
    // Abrir la web renueva el plazo.
    persistToken(token).catch(() => {})
  } catch (error) {
    // Vencida, llave borrada o dato dañado: se borra todo y se pide login de nuevo.
    attempt(() => window.localStorage.removeItem(ENCRYPTED_KEY))
    await deleteCryptoKey()
  }
}

export const getSessionToken = () =>
  memoryToken || attempt(() => window.sessionStorage.getItem(TEMPORARY_KEY))

const removeStoredTokens = () => {
  memoryToken = null
  attempt(() => window.localStorage.removeItem(ENCRYPTED_KEY))
  attempt(() => window.sessionStorage.removeItem(TEMPORARY_KEY))
}

// Cerrar sesión (o 403): sin la llave, lo que hubiera quedado guardado ya no se puede descifrar.
export const clearSessionToken = () => {
  removeStoredTokens()
  deleteCryptoKey()
}

export const saveSessionToken = (token, { persist = true } = {}) => {
  const value = JSON.stringify(token)
  removeStoredTokens()
  if (!persist) {
    attempt(() => window.sessionStorage.setItem(TEMPORARY_KEY, value))
    return
  }
  memoryToken = value
  persistToken(value)
    .catch(() => {
      // Sin Web Crypto o IndexedDB (p. ej. navegación privada) la sesión queda solo en
      // memoria: al recargar se pide login. Nunca se guarda sin cifrar.
    })
}
